/* ************************************************************************** */
/*                                Dependencies                                */
/* ************************************************************************** */

// Lib dependencies
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { formatDistance } from 'date-fns';
import upperFirst from 'lodash/upperFirst';
import { useReactiveVar } from '@apollo/client';

// UI lib components
import {
  Stack,
  Flex,
  Text,
  Spoiler,
  Card,
  Button,
  Tooltip,
} from '@mantine/core';

// UI local components
import ContentLoader from '@app/shared/ui-kit/molecules/ContentLoader';
import User from '@app/shared/ui-kit/atoms/User';
import QuestionPriorityBadge from '@app/shared/ui-kit/molecules/QuestionPriorityBadge';

// Assets
import { IconCirclePlus as AddQuestionIcon } from '@tabler/icons-react';

// API & Local types
import { SectionContentProps } from '@features/Resource/ResourceMenu/types';
import {
  useListResourceQuestionsQuery,
  WorkspaceSupportStatusType,
} from '@app/types/blackroom-api';

// Store
import dialogStoreVar from '@app/features/Dialog/store/dialogStore';
import useWorkspace from '@app/features/Workspace/store/hooks/useWorkspace';
import useAccount from '@app/features/Account/store/useAccount';

// Style
import { FONT_WEIGHT } from '@app/shared/ui-kit/fonts';
import { ICON_SIZE_MAP } from '@app/shared/ui-kit/icons-sizes';

/* ************************************************************************** */
/*                                  Component                                 */
/* ************************************************************************** */

function SectionSupport({ resource }: SectionContentProps) {
  /* ********************************** HOOKS ********************************* */

  // Localization
  const { t } = useTranslation();

  // Store
  const dialogStore = useReactiveVar(dialogStoreVar);
  const workspace = useWorkspace();

  // Auth
  const { isCurrentWorkspaceAdmin } = useAccount();

  // API
  const { data: questionsData, loading: loadingListQuestions } =
    useListResourceQuestionsQuery({
      variables: {
        filter: { workspaceId: workspace.id, resourceId: resource.id },
        limit: 1_000,
      },
    });

  // Memoize data
  const questions = useMemo(() => {
    return questionsData?.questions.nodes ?? [];
  }, [questionsData]);

  /* ******************************** RENDERING ******************************* */

  if (loadingListQuestions) {
    return <ContentLoader />;
  }

  return (
    <Stack p="xs" gap="lg">
      <Tooltip
        label={t('Support:Common.errors.readonlyAccess')}
        disabled={
          isCurrentWorkspaceAdmin ||
          workspace.supportStatus === WorkspaceSupportStatusType.Editable
        }
      >
        <Button
          leftSection={<AddQuestionIcon size={ICON_SIZE_MAP.smallavg} />}
          size="sm"
          radius="xs"
          px="sm"
          disabled={
            !isCurrentWorkspaceAdmin &&
            workspace.supportStatus !== WorkspaceSupportStatusType.Editable
          }
          onClick={() => {
            dialogStore?.invokeDialog?.('dialog-support-question', {
              modalProps: {
                title: t('Support:DialogQuestionCreate.title'),
                withCloseButton: false,
              },
              resourceId: resource.id,
            });
          }}
        >
          {upperFirst(t('Support:Common.ctaCreateQuestion'))}
        </Button>
      </Tooltip>
      <Stack gap="sm">
        {questions.map((question) => (
          <Card p="xs" shadow="md" withBorder>
            <Flex justify="space-between" pb="xs" align="center">
              <Flex gap="xs" align="center">
                <User size="sm" user={question.createdBy} />
                <Text fz="sm">
                  <Text span fw={FONT_WEIGHT.ultraLight}>
                    {upperFirst(t('Support:Common.questionCreatedAtPrefix'))}
                  </Text>{' '}
                  {formatDistance(new Date(question.createdAt), Date.now(), {
                    addSuffix: true,
                  })}
                </Text>
              </Flex>
              <QuestionPriorityBadge questionPriority={question.priority} />
            </Flex>
            <Spoiler
              maxHeight={45}
              showLabel={t('Support:Common.spoiler.showLabel')}
              hideLabel={t('Support:Common.spoiler.hideLabel')}
            >
              {question.title}
            </Spoiler>
          </Card>
        ))}
      </Stack>
    </Stack>
  );
}

export default SectionSupport;
